import { useCallback, useState } from 'react';

export type OperationType = 'upload' | 'download' | 'delete' | 'copy' | 'move';

export interface OperationError {
  type: OperationType;
  message: string;
}

export const useOperationErrors = () => {
  const [errors, setErrors] = useState<Map<OperationType, string>>(new Map());

  const setError = useCallback((type: OperationType, message: string) => {
    setErrors((prev) => {
      const newMap = new Map(prev);
      newMap.set(type, message);
      return newMap;
    });
  }, []);

  const clearError = useCallback((type: OperationType) => {
    setErrors((prev) => {
      const newMap = new Map(prev);
      newMap.delete(type);
      return newMap;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors(new Map());
  }, []);

  const getError = useCallback(
    (type: OperationType): string | null => {
      return errors.get(type) || null;
    },
    [errors]
  );

  const hasError = useCallback(
    (type: OperationType): boolean => {
      return errors.has(type);
    },
    [errors]
  );

  const getAllErrors = useCallback((): OperationError[] => {
    return Array.from(errors.entries()).map(([type, message]) => ({ type, message }));
  }, [errors]);

  return {
    setError,
    clearError,
    clearAllErrors,
    getError,
    hasError,
    getAllErrors,
  };
};
